<script setup lang="ts">
import { computed } from 'vue';
import { get } from 'lodash';
import { useI18n } from 'vue-i18n';

import { YardTaskEvent } from '@nova/core';
import UserPresenter from '@satellite/components/elements/presenters/user/UserPresenter.vue';
import useYardTaskEvent from './useYardTaskEvent';

import type { YardTaskTimelineEvent } from './types';

interface Props {
  event: YardTaskTimelineEvent;
  timezone: string;
  locale: string;
}

const props = defineProps<Props>();

const { t } = useI18n();
const { formattedCreatedTime, user } = useYardTaskEvent(props.event, {
  timezone: props.timezone,
  locale: props.locale
});

/** Both directions of the same reserve lifecycle, so one component reads them off the event. */
const isCancelled = computed(
  () => get(props.event, 'data.event') === YardTaskEvent.RESERVE_CANCELLED
);

/** "Area 1 - D09" — left to each locale string to compose, not pre-joined here (OD-4119). */
const spot = computed(
  () => get(props.event, 'data.data.spot') as { code?: string; areaName?: string } | undefined
);

const label = computed(() => {
  if (spot.value?.code && spot.value?.areaName) {
    const key = isCancelled.value
      ? 'global.timeline.cancelledTheReservationForSpot'
      : 'global.timeline.reservedSpotForThisTask';

    return t(key, { areaName: spot.value.areaName, spotCode: spot.value.code });
  }

  return t(
    isCancelled.value ? 'global.timeline.cancelledTheReserve' : 'global.timeline.reservedTheSpot'
  );
});
</script>

<template>
  <div class="d-flex flex-column gap-0-5 align-start justify-start">
    <span class="d-flex flex-row flex-wrap align-center justify-start gap-1">
      <user-presenter
        v-if="user"
        class="typography-body-sm-bold text--color-text-secondary"
        :user="user"
        :display-fields="['name']" />
      <span class="typography-body-sm">{{ label }}</span>
    </span>
    <span v-if="formattedCreatedTime" class="typography-caption text--color-text-tertiary">
      {{ formattedCreatedTime }}
    </span>
  </div>
</template>
